/**
 * 조직 API 서비스
 */
import { apiClient, validatePathParam } from './client';
import type {
  OrganizationListResponse,
  StoreListByOrgResponse,
  BrandListByOrgResponse,
  BrandStoreListResponse,
  AllStoresResponse,
  Organization,
} from './types';

export interface GetOrganizationsParams {
  page?: number;
  page_size?: number;
  search?: string;
  lat?: number;
  lng?: number;
  include?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface GetStoresByOrgParams {
  pickup_available?: boolean;
  is_active?: boolean;
  page?: number;
  page_size?: number;
  [key: string]: string | number | boolean | undefined;
}

export interface GetBrandsByOrgParams {
  is_active?: boolean;
  [key: string]: string | number | boolean | undefined;
}

export interface GetStoresByBrandParams {
  is_active?: boolean;
  [key: string]: string | number | boolean | undefined;
}

export interface GetAllStoresParams {
  limit?: number;
  org_limit?: number;
  [key: string]: string | number | boolean | undefined;
}

export const organizationApi = {
  /**
   * 조직 목록 조회
   * GET /api/v1/organizations
   */
  getOrganizations: (params?: GetOrganizationsParams) =>
    apiClient.get<OrganizationListResponse>('/api/v1/organizations', { params }),

  /**
   * 조직 상세 조회
   * GET /api/v1/organizations/:orgParam
   * @param orgParam 조직 슬러그, 닉네임(@prefix), 또는 ID
   */
  getOrganization: (orgParam: string, signal?: AbortSignal) => {
    const safeOrgParam = validatePathParam(orgParam, 'orgParam');
    return apiClient.get<{ data: Organization }>(`/api/v1/organizations/${safeOrgParam}`, { signal });
  },

  /**
   * 조직별 매장 목록 조회
   * GET /api/v1/organizations/:orgParam/stores
   * @param orgParam 조직 슬러그, 닉네임(@prefix), 또는 ID
   */
  getStoresByOrg: (orgParam: string, params?: GetStoresByOrgParams) => {
    const safeOrgParam = validatePathParam(orgParam, 'orgParam');
    return apiClient.get<StoreListByOrgResponse>(`/api/v1/organizations/${safeOrgParam}/stores`, { params });
  },

  /**
   * 조직별 브랜드 목록 조회
   * GET /api/v1/organizations/:orgParam/brands
   * @param orgParam 조직 슬러그, 닉네임(@prefix), 또는 ID
   */
  getBrandsByOrg: (orgParam: string, params?: GetBrandsByOrgParams) => {
    const safeOrgParam = validatePathParam(orgParam, 'orgParam');
    return apiClient.get<BrandListByOrgResponse>(`/api/v1/organizations/${safeOrgParam}/brands`, { params });
  },

  /**
   * 브랜드별 매장 목록 조회
   * GET /api/v1/organizations/:orgParam/brands/:brandParam/stores
   * @param orgParam 조직 슬러그, 닉네임(@prefix), 또는 ID
   * @param brandParam 브랜드 슬러그 또는 ID
   */
  getStoresByBrand: (orgParam: string, brandParam: string, params?: GetStoresByBrandParams) => {
    const safeOrgParam = validatePathParam(orgParam, 'orgParam');
    const safeBrandParam = validatePathParam(brandParam, 'brandParam');
    return apiClient.get<BrandStoreListResponse>(`/api/v1/organizations/${safeOrgParam}/brands/${safeBrandParam}/stores`, { params });
  },

  /**
   * 전체 매장 일괄 조회 (N+1 API 호출 최적화)
   * GET /api/v1/stores/all
   * 모든 활성 조직의 매장을 한 번의 요청으로 조회
   */
  getAllStores: (params?: GetAllStoresParams) =>
    apiClient.get<AllStoresResponse>('/api/v1/stores/all', { params }),
};
